import { PageHeader } from "@shared/components/PageHeader";
import { Stepper } from "@shared/components/Stepper";
import { useToast } from "@shared/hooks/use-toast";
import { ArrowLeft } from "lucide-react";
import React, { useCallback, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import CriticalityAssessmentStep from "./CriticalityAssessmentStep";
import DocumentationStep from "./DocumentationStep";
import NetworkDetailsStep from "./NetworkDetailsStep";

interface AddNetworkLandingProps {
  editMode?: boolean;
  networkId?: string;
}

const AddNetworkLanding: React.FC<AddNetworkLandingProps> = ({
  editMode = false,
  networkId,
}) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { id, action } = useParams();

  // Track completion status for navigation
  const [completionData, setCompletionData] = useState<{
    networkName?: string;
    networkId?: string;
  }>({});

  const handleComplete = useCallback(
    async (allStepData: Record<string, any>) => {
      try {
        toast({
          title:
            action === "edit"
              ? "Network Updated Successfully"
              : "Network Created Successfully",
        });

        setTimeout(() => {
          navigate("/ax/asset-registry/networks");
        }, 1500);
      } catch (error) {
        toast({
          title: "Error",
          description: editMode
            ? "Failed to update network"
            : "Failed to create network",
          variant: "destructive",
        });
      }
    },
    [editMode, toast, navigate, completionData]
  );

  const handleStepComplete = useCallback(
    (stepId: string, stepData: any) => {
      if (stepId === "network-details" && stepData) {
        setCompletionData({
          networkName: stepData.networkName,
          networkId: stepData.networkId || stepData.id || id,
        });
      }
    },
    [id]
  );

  const stepperSteps = useMemo(
    () => [
      {
        id: "network-details",
        title: "Network Details",
        description: "Basic network information",
        component: (
          <NetworkDetailsStep
            editMode={editMode}
            onStepComplete={(stepData) =>
              handleStepComplete("network-details", stepData)
            }
          />
        ),
        slug: "network-details",
      },
      {
        id: "criticality-assessment",
        title: "Criticality Assessment",
        description: "Risk and priority evaluation",
        component: (
          <CriticalityAssessmentStep
            editMode={editMode}
            networkId={completionData.networkId || id || networkId} // Use fallbacks
          />
        ),
        slug: "criticality-assessment",
      },
      {
        id: "documentation",
        title: "Documentation",
        description: "Upload supporting documents",
        component: <DocumentationStep editMode={editMode} />,
        slug: "documentation",
      },
    ],
    [editMode, completionData.networkId, id]
  );

  return (
    <div className="min-h-screen p-6">
      <div className="space-y-6">
        <div className="mb-4 flex items-center gap-4">
          <ArrowLeft
            className="w-5 h-5 cursor-pointer text-gray-700"
            onClick={() => navigate("/ax/asset-registry/networks")}
          />

          <div className="flex flex-col">
            <PageHeader
              title={action === "edit" ? `Edit Network` : "Add Network"}
              subtitle={
                editMode
                  ? `Update network record: ${networkId || id}`
                  : "Create a new network asset for your water utility system"
              }
            />
          </div>
        </div>

        <Stepper
          steps={stepperSteps}
          onComplete={handleComplete}
          autoSave={false}
          persistProgress={false}
          storageKey={
            editMode
              ? `edit-network-${networkId || id}`
              : "add-network-progress"
          }
          allowSkip={false}
          enableRouting={true}
          className="w-full"
          allowStepClick={true}
          onStepChange={undefined}
          onValidation={undefined}
          onStepSave={undefined}
        />
      </div>
    </div>
  );
};

export default AddNetworkLanding;
